import { Box, Typography, Button, Stack, Divider, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import LaptopIcon from '@mui/icons-material/Laptop';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DownloadIcon from '@mui/icons-material/Download';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';

const DetailItem = ({ icon: Icon, label, value }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5,
    p: 1.5, borderRadius: '12px', bgcolor: '#F8FAFF',
    border: '1px solid #EEF2FF'
  }}>
    <Box sx={{ color: '#0E2465', display: 'flex', alignItems: 'center' }}>
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box>
      <Typography variant="caption" color="#737791" display="block">{label}</Typography>
      <Typography variant="body2" fontWeight="bold" color="#0E2465">{value}</Typography>
    </Box>
  </Box>
);

const BookingConfirmation = ({
  booking, service, ownerUser,
  selectedDate, selectedTime,
  selectedLanguage, selectedModality,
  paymentMethod
}) => {
  const navigate = useNavigate();

  const bookingRef = booking?._id
    ? `BK-${new Date().getFullYear()}-${booking._id.slice(-5).toUpperCase()}`
    : 'BK-PEND';

  const formatDateLong = (date) => {
    if (!date) return '';
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const d = new Date(date);
    return `${days[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  };

  const getPaymentLabel = (method) => {
    if (method === 'paypal') return 'PayPal';
    if (method === 'google') return 'Google Pay';
    return 'Tarjeta de crédito/débito';
  };

  const total = Number(service?.costoServicio || 0).toFixed(2);
  const authorName = ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : 'Proveedor';

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 12, mb: 8, px: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: '50%',
          bgcolor: '#22C55E', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', mb: 2
        }}>
          <CheckIcon sx={{ color: 'white', fontSize: 42 }} />
        </Box>
        <Typography variant="h4" fontWeight="900" color="#0E2465" sx={{ lineHeight: 1.2 }}>
          Tu servicio quedó agendado
        </Typography>
      </Box>

      <Box sx={{ bgcolor: 'white', borderRadius: '24px', border: '1px solid #F1F5F9', p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, pr: 1 }}>
            <Typography variant="subtitle1" fontWeight="900" color="#0E2465" sx={{ lineHeight: 1.3 }}>
              {service?.nombre}
            </Typography>
            <Typography variant="body2" color="#737791">Por {authorName}</Typography>
          </Box>
          <Chip
            label={bookingRef}
            size="small"
            sx={{ bgcolor: '#0E2465', color: 'white', fontWeight: 600, fontSize: '0.62rem', flexShrink: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 2, borderColor: '#F1F5F9' }} />

        <Typography variant="subtitle2" fontWeight="bold" color="#0E2465" sx={{ mb: 1.5 }}>
          Detalles de la cita
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
          <DetailItem icon={CalendarTodayIcon} label="Fecha" value={formatDateLong(selectedDate)} />
          <DetailItem icon={AccessTimeIcon} label="Hora" value={selectedTime ? `${selectedTime} hrs` : '-'} />
          <DetailItem icon={LaptopIcon} label="Modalidad" value={selectedModality} />
          <DetailItem icon={LanguageIcon} label="Idioma" value={selectedLanguage} />
        </Box>

        <Box sx={{ bgcolor: '#F8FAFF', borderRadius: '12px', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="#737791">Subtotal</Typography>
            <Typography variant="body2" color="#0E2465" fontWeight={600}>${total}</Typography>
          </Box>
          <Divider sx={{ my: 1, borderColor: '#EEF2FF' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CreditCardIcon sx={{ fontSize: 14, color: '#737791' }} />
              <Typography variant="caption" color="#737791">{getPaymentLabel(paymentMethod)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="#737791" display="block">Total pagado</Typography>
              <Typography variant="subtitle1" fontWeight="900" color="#0E2465">${total}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Button
        variant="outlined"
        fullWidth
        startIcon={<DownloadIcon />}
        sx={{
          mb: 3, py: 1.5, borderRadius: '14px', textTransform: 'none',
          fontWeight: 700, borderColor: '#E2E8F0', color: '#0E2465',
          '&:hover': { borderColor: '#0E2465', bgcolor: '#F8FAFF' }
        }}
      >
        Descargar comprobante
      </Button>

      <Box sx={{ bgcolor: 'white', borderRadius: '24px', border: '1px solid #F1F5F9', p: 3, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" color="#0E2465" sx={{ mb: 2 }}>
          ¿Qué sigue ahora?
        </Typography>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', bgcolor: '#EFF6FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <EmailIcon sx={{ fontSize: 18, color: '#0E2465' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="bold" color="#0E2465">Revisa tu correo</Typography>
              <Typography variant="caption" color="#737791">
                Te enviamos un recibo detallado y el enlace de acceso a tu servicio.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', bgcolor: '#EFF6FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <NotificationsIcon sx={{ fontSize: 18, color: '#0E2465' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="bold" color="#0E2465">Recordatorios activos</Typography>
              <Typography variant="caption" color="#737791">
                Recibirás una notificación 24 horas y 1 hora antes de que comience el servicio.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/buscar')}
          sx={{
            py: 1.5, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
            borderColor: '#E2E8F0', color: '#0E2465',
            '&:hover': { borderColor: '#0E2465', bgcolor: '#F8FAFF' }
          }}
        >
          Explorar más servicios
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate('/home')}
          sx={{
            py: 1.5, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
            bgcolor: '#0E2465', color: 'white',
            boxShadow: '0px 8px 16px rgba(14,36,101,0.2)',
            '&:hover': { bgcolor: '#0a1a4a' }
          }}
        >
          Ir a mis servicios
        </Button>
      </Box>
    </Box>
  );
};

export default BookingConfirmation;
